import { useEffect, useRef, useState } from "react";
import { useNotificationContext } from "cerulean";
import { useObjectMemo } from "byzantine";
import ApiHttp from "byzantine/src/ApiHttp";
import { useLocalization } from "@fluent/react";
import { useInstitution } from "byzantine/src/dbbl/businessLogic/entities";

interface CardListResponse {
  results: API.Card[];
}

const useFetchCards = (): { isLoading: boolean; cards: API.Card[] } => {
  const initRef = useRef<boolean>(false);
  const [cards, setCards] = useState<API.Card[]>([]);
  const institution = useInstitution();
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const { sendNotification } = useNotificationContext();
  const { l10n } = useLocalization();

  useEffect(() => {
    async function fetchCards(): Promise<void> {
      setIsLoading(true);

      try {
        const response = await ApiHttp.fetch<CardListResponse>(
          "cards",
          { method: "GET", headers: { "API-Version": "0017" } },
          {}
        );
        const results = response?.results ?? [];
        setCards(results);
      } catch {
        sendNotification({
          type: "negative",
          text: l10n.getString(
            "error-unknown",
            undefined,
            "Something went wrong"
          ),
        });
        setCards([]);
      } finally {
        setIsLoading(false);
      }
    }

    if (institution?.features?.cards && !initRef.current) {
      initRef.current = true;
      fetchCards();
    }
  }, [sendNotification, l10n, institution?.features?.cards]);

  return useObjectMemo({ isLoading, cards });
};

export default useFetchCards;
